/**
 * Checkout-summary VAT/reverse-charge decision (Task 4 — frontend VAT, display only).
 *
 * Mirrors `invoiceVatTotals.ts`'s reverse-charge collapsing rule, but BEFORE a charge exists:
 * there's no frozen `vat_rate`/`vat_amount` yet, so this computes a DISPLAY ESTIMATE from the
 * billing country's rate (`vatApi.getVatEstimate`) via `vatMoney`'s integer-cents `grossCents` —
 * the backend recomputes VAT authoritatively at signup (`signUpBody.ts` never sends a `vat`
 * amount); this only shows the member what to expect.
 *
 * Only a CONFIRMED reverse charge — business member type AND a VIES `'valid'` result — collapses
 * to the reverse-charge line. Every other combination (personal, business+unavailable,
 * business+invalid, business+checking/idle) renders as a normal taxable VAT line: VIES
 * `'unavailable'` is explicitly NOT reverse-charged (see `vatFieldState`'s doc), and an
 * invalid/not-yet-confirmed business order isn't submittable anyway (the Yup gate in
 * `personinfo.tsx` blocks it) — showing it as taxable here is the conservative default, never
 * UNDER-stating what the member may end up paying.
 */

import {grossCents} from './vatMoney'

export interface CheckoutVatSummaryInput {
  /** Net subtotal, in integer cents. */
  subtotalCents: number
  /** VAT rate as a plain percentage (e.g. 19 for 19%), from the billing country's estimate. */
  rate: number
  memberType: string
  vatStatus: string
}

export interface CheckoutVatSummary {
  line: 'vat' | 'reverse_charge'
  reverseCharge: boolean
  /** VAT amount, in integer cents. Always 0 when `reverseCharge`. */
  vatCents: number
  /** `subtotalCents + vatCents` — net total when `reverseCharge`, gross total otherwise. */
  totalCents: number
}

export function checkoutVatSummary({
  subtotalCents,
  rate,
  memberType,
  vatStatus,
}: CheckoutVatSummaryInput): CheckoutVatSummary {
  const reverseCharge = memberType === 'business' && vatStatus === 'valid'

  if (reverseCharge) {
    return {line: 'reverse_charge', reverseCharge: true, vatCents: 0, totalCents: subtotalCents}
  }

  const totalCents = grossCents(subtotalCents, rate * 100)
  return {line: 'vat', reverseCharge: false, vatCents: totalCents - subtotalCents, totalCents}
}
